import type { ISerialize } from '@/objects/ISerialize';
import { formatDateIso } from '@/utilities/dateutils';

export class RankedTime implements ISerialize {
    public AccumulatedTime: number;

    public AccumulatedTimeSmooth: number;

    public ClockTime: Date;

    public EventID: number;

    public IsStartingTime: boolean;

    public LapNumber: number;

    public PenaltyString: string | null;

    public RaceContestantID: number;

    public StationRoleID: number;

    public TUID: string | null;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        AccumulatedTime?: number;
        AccumulatedTimeSmooth?: number;
        ClockTime?: Date;
        EventID?: number;
        IsStartingTime?: boolean;
        LapNumber?: number;
        PenaltyString?: string | null;
        RaceContestantID?: number;
        StationRoleID?: number;
        TUID?: string | null;
    }) {
        this.AccumulatedTime = values.AccumulatedTime ?? 0;

        this.AccumulatedTimeSmooth = values.AccumulatedTimeSmooth ?? 0;

        this.ClockTime = values.ClockTime ?? new Date('1970-01-01T00:00:00.000');

        this.EventID = values.EventID ?? 0;

        this.IsStartingTime = values.IsStartingTime ?? false;

        this.LapNumber = values.LapNumber ?? 0;

        this.PenaltyString = values.PenaltyString ?? '';

        this.RaceContestantID = values.RaceContestantID ?? 0;

        this.StationRoleID = values.StationRoleID ?? 0;

        this.TUID = values.TUID ?? '';
    }

    public serialize(): string {
        return (
            '{' +
            `"AccumulatedTime": ${JSON.stringify(this.AccumulatedTime)}` +
            ',' +
            `"AccumulatedTimeSmooth": ${JSON.stringify(this.AccumulatedTimeSmooth)}` +
            ',' +
            `"ClockTime": ${'"' + formatDateIso(this.ClockTime) + '"'}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"IsStartingTime": ${JSON.stringify(this.IsStartingTime)}` +
            ',' +
            `"LapNumber": ${JSON.stringify(this.LapNumber)}` +
            ',' +
            `"PenaltyString": ${JSON.stringify(this.PenaltyString)}` +
            ',' +
            `"RaceContestantID": ${JSON.stringify(this.RaceContestantID)}` +
            ',' +
            `"StationRoleID": ${JSON.stringify(this.StationRoleID)}` +
            ',' +
            `"TUID": ${JSON.stringify(this.TUID)}` +
            '}'
        );
    }

    /**
     * Creates an instance of RankedTime from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        AccumulatedTime?: number;
        AccumulatedTimeSmooth?: number;
        ClockTime?: Date;
        EventID?: number;
        IsStartingTime?: boolean;
        LapNumber?: number;
        PenaltyString?: string | null;
        RaceContestantID?: number;
        StationRoleID?: number;
        TUID?: string | null;
    }): RankedTime {
        return new RankedTime(values);
    }

    public static _FromServerJson(json: any): RankedTime {
        return new RankedTime({
            AccumulatedTime: json.AccumulatedTime,
            AccumulatedTimeSmooth: json.AccumulatedTimeSmooth,
            ClockTime: json.ClockTime instanceof Date ? json.ClockTime : new Date(json.ClockTime),
            EventID: json.EventID,
            IsStartingTime: json.IsStartingTime,
            LapNumber: json.LapNumber,
            PenaltyString: json.PenaltyString ?? '',
            RaceContestantID: json.RaceContestantID,
            StationRoleID: json.StationRoleID,
            TUID: json.TUID ?? '',
        });
    }

    /**
     * Creates an instance of RankedTime from another instance of RankedTime.
     * @param other The RankedTime you want to clone
     * @returns A cloned RankedTime
     */
    public static FromOther(other: RankedTime): RankedTime {
        return RankedTime._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return RankedTime.FromOther(this);
    }

    static NAME_OF_CLASS = 'RankedTime';
}
